/**
 * Marketplace categories (docs/Decisions.md D68 items 2, 5, 7 and 9), for an
 * organization (Org marketplace -> Categories) or for the platform (Platform
 * admin -> Marketplace).
 *
 * An org admin sets each category's lead fee and organization fee inside the
 * platform admin's bounds, imports platform categories (which keep their
 * review policy and requirements) or builds its own tree. An investment
 * organization imports only, and charges flat fees only. The platform admin
 * sets the review policy and requirements of platform categories and marks
 * which ones investment organizations may use.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useConfirm } from '../lib/confirm';
import { useToast } from './toast';
import { Spinner, ErrorAlert, EmptyState, Field, TextareaField, SelectField } from './ui';
import {
  LIMITS,
  REQUIREMENT_KINDS,
  REQUIREMENT_KIND_LABELS,
  REVIEW_POLICY_IDS,
  REVIEW_POLICY_LABELS,
  amountToCents,
  centsToInput,
  computeFeeSplit,
  feeLabel,
  formatUsd,
} from '../lib/marketplace';
import { fieldErrorsFrom, focusFirstInvalid } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

function feeToForm(fee) {
  if (!fee) return { type: 'none', value: '' };
  return fee.type === 'percent'
    ? { type: 'percent', value: String(fee.percent) }
    : { type: 'fixed', value: centsToInput(fee.amountCents) };
}

function formToFee(f) {
  if (f.type === 'none') return { fee: null };
  if (f.type === 'percent') {
    const n = Number(f.value);
    return f.value.trim() === '' || !Number.isFinite(n)
      ? { error: 'Enter a percentage such as 4 or 2.5' }
      : { fee: { type: 'percent', percent: n } };
  }
  const cents = amountToCents(f.value);
  return Number.isNaN(cents)
    ? { error: 'Enter an amount such as 50 or 50.00' }
    : { fee: { type: 'fixed', amountCents: cents } };
}

function FeeInput({ label, hint, value, onChange, fixedOnly, error, of }) {
  return (
    <fieldset className="mb-2">
      <legend className={ui.label}>{label}</legend>
      <div className="grid gap-x-3 md:grid-cols-2">
        <SelectField
          label="Kind"
          value={value.type}
          onChange={e => onChange({ ...value, type: e.target.value })}
        >
          <option value="none">None</option>
          <option value="fixed">Fixed amount</option>
          {!fixedOnly && <option value="percent">Percentage of {of}</option>}
        </SelectField>
        <Field
          label={value.type === 'percent' ? 'Percent' : 'Amount (USD)'}
          inputMode="decimal"
          value={value.value}
          disabled={value.type === 'none'}
          onChange={e => onChange({ ...value, value: e.target.value })}
          error={error}
          hint={hint}
        />
      </div>
    </fieldset>
  );
}

function RequirementsEditor({ value, onChange, error }) {
  return (
    <fieldset className="mb-4">
      <legend className={ui.label}>Requirements vendors must meet</legend>
      <p className={cn(ui.hint, 'mt-0')}>
        Licenses, permits or documents a vendor gives when applying; regulated and licensed policies
        need each one validated before approval.
      </p>
      {value.map((r, i) => (
        <div key={i} className="grid gap-x-3 md:grid-cols-[2fr_1fr_auto] md:items-end">
          <Field
            label={`Requirement ${i + 1}`}
            value={r.label}
            maxLength={LIMITS.requirementLabelMax}
            onChange={e =>
              onChange(value.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
            }
          />
          <SelectField
            label="Kind"
            value={r.kind}
            onChange={e =>
              onChange(value.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))
            }
          >
            {REQUIREMENT_KINDS.map(k => (
              <option key={k} value={k}>
                {REQUIREMENT_KIND_LABELS[k]}
              </option>
            ))}
          </SelectField>
          <button
            type="button"
            className={cn(ui.btnGhost, ui.btnSm, 'mb-4')}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      {error && <p className={ui.fieldError}>{error}</p>}
      <button
        type="button"
        className={cn(ui.btnSecondary, ui.btnSm)}
        disabled={value.length >= LIMITS.requirementsPerCategory}
        onClick={() => onChange([...value, { label: '', kind: 'license' }])}
      >
        Add a requirement
      </button>
    </fieldset>
  );
}

/** What the fees mean on a sample deal (D68 items 1 to 3), computed by the shared rules. */
function FeePreview({ leadFee, orgFee, policy }) {
  const lead = formToFee(leadFee);
  const org = formToFee(orgFee);
  if (lead.error || org.error || !lead.fee || !policy) return null;
  const deal = 250000;
  const split = computeFeeSplit(
    { leadFee: lead.fee, orgFee: org.fee },
    { dealValueCents: deal, policy, salesperson: { salespersonId: 'example' } }
  );
  return (
    <p className={cn(ui.alertInfo, 'mb-4')}>
      On a {formatUsd(deal)} deal won through this organization, the vendor pays a lead fee of{' '}
      {formatUsd(split.leadFeeCents)}: {formatUsd(split.orgShareCents)} to the organization,{' '}
      {formatUsd(split.platformFeeCents)} to the platform, and {formatUsd(split.balanceCents)} to
      the salesperson who sold it (not charged when the vendor sold it itself).
    </p>
  );
}

function CategoryEditor({ category, owner, investment, topLevel, policy, onSaved, onCancel }) {
  const toast = useToast();
  const formRef = useRef(null);
  const imported = Boolean(category?.sourceCategoryId);
  const [form, setForm] = useState(() => ({
    name: category?.name || '',
    description: category?.description || '',
    parentId: category?.parentId || '',
    active: category ? category.active !== false : true,
    reviewPolicy: category?.reviewPolicy || 'standard',
    requirements: category?.requirements || [],
    investmentAllowed: Boolean(category?.investmentAllowed),
    leadFee: feeToForm(category?.leadFee),
    orgFee: feeToForm(category?.orgFee),
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  async function save(e) {
    e.preventDefault();
    const body = {
      name: form.name,
      description: form.description,
      active: form.active,
    };
    if (!(investment && imported)) body.parentId = form.parentId || null;
    if (!imported) {
      body.reviewPolicy = form.reviewPolicy;
      body.requirements = form.requirements;
    }
    if (owner === 'platform') {
      body.investmentAllowed = form.investmentAllowed;
      if (!category) body.scope = 'platform';
    } else {
      const lead = formToFee(form.leadFee);
      const org = formToFee(form.orgFee);
      if (lead.error || org.error) {
        setErrors({ leadFee: lead.error, orgFee: org.error });
        return;
      }
      body.leadFee = lead.fee;
      body.orgFee = org.fee;
    }
    setSaving(true);
    try {
      const data = category
        ? await marketApi.updateCategory(category.categoryId, body)
        : await marketApi.createCategory(body);
      toast.success(`${data.category.name} saved`);
      onSaved(data.category);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      focusFirstInvalid(formRef.current);
      toast.error(err.message || 'The category was not saved');
    } finally {
      setSaving(false);
    }
  }

  const parents = topLevel.filter(c => c.categoryId !== category?.categoryId);
  return (
    <form ref={formRef} onSubmit={save} className={cn(ui.cardFlat, 'mt-3')} noValidate>
      <Field
        label="Name"
        value={form.name}
        maxLength={LIMITS.nameMax}
        onChange={e => set('name', e.target.value)}
        error={errors.name}
      />
      <TextareaField
        label="Description"
        rows={2}
        value={form.description}
        maxLength={LIMITS.descriptionMax}
        onChange={e => set('description', e.target.value)}
        error={errors.description}
      />
      {!(investment && imported) && (category?.children || []).length === 0 && (
        <SelectField
          label="Inside"
          value={form.parentId}
          onChange={e => set('parentId', e.target.value)}
          error={errors.parentId}
        >
          <option value="">Top level</option>
          {parents.map(c => (
            <option key={c.categoryId} value={c.categoryId}>
              {c.name}
            </option>
          ))}
        </SelectField>
      )}
      {owner === 'org' && (
        <>
          <FeeInput
            label="Lead fee, charged to the vendor on a deal won through this organization"
            of="the deal value"
            value={form.leadFee}
            fixedOnly={investment}
            onChange={v => set('leadFee', v)}
            error={errors.leadFee}
          />
          <FeeInput
            label="Organization fee, the part of the lead fee that comes to the organization"
            of="the lead fee"
            hint="The platform fee is taken from this."
            value={form.orgFee}
            fixedOnly={investment}
            onChange={v => set('orgFee', v)}
            error={errors.orgFee}
          />
          <FeePreview leadFee={form.leadFee} orgFee={form.orgFee} policy={policy} />
        </>
      )}
      {imported ? (
        <p className={cn(ui.hint, 'mb-4')}>
          Imported from the platform list: it keeps the{' '}
          {REVIEW_POLICY_LABELS[form.reviewPolicy].label.toLowerCase()} review policy
          {form.requirements.length ? ` and its ${form.requirements.length} requirements` : ''}.
        </p>
      ) : (
        <>
          <SelectField
            label="Review policy"
            value={form.reviewPolicy}
            onChange={e => set('reviewPolicy', e.target.value)}
            hint={REVIEW_POLICY_LABELS[form.reviewPolicy].help}
            error={errors.reviewPolicy}
          >
            {REVIEW_POLICY_IDS.map(id => (
              <option key={id} value={id}>
                {REVIEW_POLICY_LABELS[id].label}
              </option>
            ))}
          </SelectField>
          <RequirementsEditor
            value={form.requirements}
            onChange={v => set('requirements', v)}
            error={errors.requirements}
          />
        </>
      )}
      {owner === 'platform' && (
        <label className="mb-2 flex items-center gap-2 text-body-s">
          <input
            type="checkbox"
            className={ui.checkbox}
            checked={form.investmentAllowed}
            onChange={e => set('investmentAllowed', e.target.checked)}
          />
          Investment organizations may use this category
        </label>
      )}
      <label className="mb-4 flex items-center gap-2 text-body-s">
        <input
          type="checkbox"
          className={ui.checkbox}
          checked={form.active}
          onChange={e => set('active', e.target.checked)}
        />
        Shown in the directory
      </label>
      <div className={ui.row}>
        <button className={ui.btnPrimary} type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save category'}
        </button>
        <button className={ui.btnGhost} type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ImportPanel({ investment, existing, onImported }) {
  const toast = useToast();
  const [platform, setPlatform] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    marketApi
      .listCategories({ scope: 'platform' })
      .then(d => setPlatform(d.categories))
      .catch(setError);
  }, []);

  if (error) return <ErrorAlert error={error} />;
  if (!platform) return <Spinner label="Loading the platform list..." />;
  const have = new Set(existing.map(c => c.sourceCategoryId).filter(Boolean));
  const usable = c => !investment || c.investmentAllowed;
  const rows = platform.flatMap(c => [c, ...c.children]).filter(c => usable(c));

  async function submit() {
    setSaving(true);
    try {
      const data = await marketApi.importCategories([...picked]);
      toast.success(`${data.imported.length} categories imported`);
      setPicked(new Set());
      onImported();
    } catch (err) {
      toast.error(err.message || 'The categories were not imported');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={cn(ui.cardFlat, 'mt-4')} aria-labelledby="import-heading">
      <h3 id="import-heading" className="mt-0 text-title-s">
        Import from the platform list
      </h3>
      <p className={cn(ui.hint, 'mt-0')}>
        {investment
          ? 'Investment organizations use the categories the platform allows for them.'
          : 'Start from the categories documented by the platform; a sub-category brings its parent.'}
      </p>
      {rows.length === 0 ? (
        <EmptyState message="The platform has no categories for you to import yet." />
      ) : (
        <fieldset>
          <legend className="sr-only">Platform categories</legend>
          {rows.map(c => (
            <label key={c.categoryId} className={cn(ui.row, 'text-body-s')}>
              <input
                type="checkbox"
                className={ui.checkbox}
                disabled={have.has(c.categoryId)}
                checked={have.has(c.categoryId) || picked.has(c.categoryId)}
                onChange={e =>
                  setPicked(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(c.categoryId);
                    else next.delete(c.categoryId);
                    return next;
                  })
                }
              />
              {c.parentId ? `- ${c.name}` : c.name}
              <span className={ui.pillNeutral}>
                {REVIEW_POLICY_LABELS[c.reviewPolicy || 'standard'].label}
              </span>
              {have.has(c.categoryId) && <span className={ui.hint}>imported</span>}
            </label>
          ))}
        </fieldset>
      )}
      <button
        type="button"
        className={cn(ui.btnPrimary, 'mt-3')}
        disabled={saving || picked.size === 0}
        onClick={submit}
      >
        {saving ? 'Importing...' : `Import ${picked.size || ''} categories`}
      </button>
    </section>
  );
}

export default function MarketplaceCategoriesPanel({ owner = 'org', investment = false }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(
        await marketApi.listCategories(owner === 'platform' ? { scope: 'platform' } : undefined)
      );
    } catch (err) {
      setError(err);
    }
  }, [owner]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Spinner label="Loading categories..." />;

  const tree = data.categories;
  const flat = tree.flatMap(c => [c, ...c.children]);
  const policy = data.feePolicy;

  async function remove(c) {
    const ok = await confirm({
      title: `Delete ${c.name}?`,
      message: 'Only a category with no sub-categories and no listed vendors can be deleted.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await marketApi.deleteCategory(c.categoryId);
      toast.success(`${c.name} deleted`);
      load();
    } catch (err) {
      toast.error(err.message || 'The category was not deleted');
    }
  }

  const saved = () => {
    setEditing(null);
    load();
  };

  return (
    <section>
      {owner === 'org' && policy && (
        <p className={cn(ui.hint, 'mt-0 mb-3')}>
          Platform limits: a lead fee up to {formatUsd(policy.maxLeadFeeCents)}
          {investment ? '' : ` or ${policy.maxLeadFeePercent}% of the deal value`}, an organization
          fee up to {formatUsd(policy.maxOrgFeeCents)}
          {investment ? '' : ` or ${policy.maxOrgFeePercent}% of the lead fee`}. The platform fee is{' '}
          {policy.platformFeePercent}% of the organization fee
          {policy.platformFeeMinCents ? `, at least ${formatUsd(policy.platformFeeMinCents)}` : ''},
          at most {formatUsd(policy.platformFeeMaxCents)}, taken from the organization fee.
        </p>
      )}
      {flat.length === 0 ? (
        <EmptyState
          message={
            owner === 'platform'
              ? 'No platform categories yet.'
              : 'No categories yet. Import some or add your own.'
          }
        />
      ) : (
        <ul className="m-0 list-none p-0">
          {flat.map(c => (
            <li
              key={c.categoryId}
              className={cn('border-b border-z-line py-3', c.parentId && 'pl-6')}
            >
              <div className={cn(ui.row, ui.spread)}>
                <span>
                  <strong>{c.name}</strong>{' '}
                  <span className={ui.pillNeutral}>
                    {REVIEW_POLICY_LABELS[c.reviewPolicy || 'standard'].label}
                  </span>
                  {c.active === false && <span className={cn(ui.pillWarning, 'ml-2')}>Hidden</span>}
                  {owner === 'platform' && c.investmentAllowed && (
                    <span className={cn(ui.pillInfo, 'ml-2')}>Investment</span>
                  )}
                  {owner === 'org' && (
                    <span className={cn(ui.hint, 'block')}>
                      Lead fee {feeLabel(c.leadFee, 'the deal value')}; organization fee{' '}
                      {feeLabel(c.orgFee, 'the lead fee')}
                    </span>
                  )}
                </span>
                {editing !== c.categoryId && (
                  <span className={ui.row}>
                    <button
                      type="button"
                      className={cn(ui.btnSecondary, ui.btnSm)}
                      onClick={() => setEditing(c.categoryId)}
                      aria-label={`Edit ${c.name}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={cn(ui.btnGhost, ui.btnSm)}
                      onClick={() => remove(c)}
                      aria-label={`Delete ${c.name}`}
                    >
                      Delete
                    </button>
                  </span>
                )}
              </div>
              {editing === c.categoryId && (
                <CategoryEditor
                  category={c}
                  owner={owner}
                  investment={investment}
                  topLevel={tree}
                  policy={policy}
                  onSaved={saved}
                  onCancel={() => setEditing(null)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {editing === '__new' ? (
        <CategoryEditor
          owner={owner}
          investment={investment}
          topLevel={tree}
          policy={policy}
          onSaved={saved}
          onCancel={() => setEditing(null)}
        />
      ) : (
        !(owner === 'org' && investment) && (
          <button
            type="button"
            className={cn(ui.btnSecondary, 'mt-4')}
            onClick={() => setEditing('__new')}
          >
            Add a category
          </button>
        )
      )}
      {owner === 'org' && <ImportPanel investment={investment} existing={flat} onImported={load} />}
    </section>
  );
}
